import { useState, useEffect } from 'react';
import { workshopApi } from '../services/api';
import {
  Job,
  JobEstado,
  JobPriority,
  OperarioStatus,
  OperarioEstado,
  getJobEstadoName,
  getJobEstadoColor,
  getPriorityName,
  getOperarioEstadoName,
  getOperarioEstadoColor,
} from '../types/models';
import { format } from 'date-fns';
import { es } from 'date-fns/locale';
import {
  Car,
  Clock,
  User,
  CheckCircle,
  AlertTriangle,
  Users,
  Wrench,
} from 'lucide-react';

export default function WorkshopPage() {
  const [jobs, setJobs] = useState<Job[]>([]);
  const [selectedJob, setSelectedJob] = useState<Job | null>(null);
  const [operarioStatuses, setOperarioStatuses] = useState<OperarioStatus[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [filterEstado, setFilterEstado] = useState<number | null>(null);

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      const [jobsData, statusesData] = await Promise.all([
        workshopApi.getJobs(filterEstado ? { estado_id: filterEstado } : {}) as Promise<Job[]>,
        workshopApi.getOperarioStatuses() as Promise<OperarioStatus[]>,
      ]);
      setJobs(jobsData || []);
      setOperarioStatuses(statusesData || []);
    } catch (error) {
      console.error('Error loading workshop data:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleSelectJob = async (jobId: string) => {
    try {
      const job = await workshopApi.getJob(jobId) as Job;
      setSelectedJob(job);
    } catch (error) {
      console.error('Error loading job details:', error);
    }
  };

  const handleAssignOperario = async (jobId: string, operarioId: string) => {
    try {
      await workshopApi.assignOperario(jobId, operarioId);
      await loadData();
      if (selectedJob) {
        handleSelectJob(selectedJob.id);
      }
    } catch (error) {
      console.error('Error assigning operario:', error);
    }
  };

  const handleStartStep = async (jobId: string, stepId: string) => {
    try {
      await workshopApi.startStep(jobId, stepId);
      if (selectedJob) {
        handleSelectJob(selectedJob.id);
      }
      await loadData();
    } catch (error) {
      console.error('Error starting step:', error);
    }
  };

  const handleCompleteStep = async (jobId: string, stepId: string) => {
    try {
      await workshopApi.completeStep(jobId, stepId);
      if (selectedJob) {
        handleSelectJob(selectedJob.id);
      }
      await loadData();
    } catch (error) {
      console.error('Error completing step:', error);
    }
  };

  const filteredJobs = filterEstado
    ? jobs.filter(j => j.estado_id === filterEstado)
    : jobs;

  const operariosLibres = operarioStatuses.filter(s => s.status_id === OperarioEstado.LIBRE);

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <div>
      <div className="mb-6">
        <h1 className="text-2xl font-bold text-gray-800 flex items-center gap-2">
          <Wrench className="h-6 w-6" />
          Panel de Taller
        </h1>
        <p className="text-gray-500">Gestión de trabajos y operarios</p>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Lista de Jobs */}
        <div className="lg:col-span-2 bg-white rounded-xl shadow-sm">
          <div className="p-4 border-b border-gray-100">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-lg font-semibold text-gray-800">Jobs</h2>
              <select
                value={filterEstado || ''}
                onChange={(e) => setFilterEstado(e.target.value ? Number(e.target.value) : null)}
                className="px-3 py-2 border border-gray-300 rounded-lg text-sm"
              >
                <option value="">Todos</option>
                <option value="10">Recibido</option>
                <option value="20">Diagnóstico</option>
                <option value="40">En Proceso</option>
                <option value="50">Espera</option>
                <option value="60">Terminado</option>
              </select>
            </div>
          </div>

          <div className="divide-y divide-gray-100 max-h-[600px] overflow-y-auto">
            {filteredJobs.length === 0 ? (
              <div className="p-8 text-center text-gray-500">
                No hay jobs con el filtro seleccionado
              </div>
            ) : (
              filteredJobs.map((job) => (
                <div
                  key={job.id}
                  onClick={() => handleSelectJob(job.id)}
                  className={`p-4 cursor-pointer hover:bg-gray-50 transition ${
                    selectedJob?.id === job.id ? 'bg-blue-50' : ''
                  }`}
                >
                  <div className="flex items-center justify-between mb-2">
                    <div className="flex items-center gap-3">
                      <Car className="h-5 w-5 text-gray-400" />
                      <span className="font-semibold text-gray-800">
                        {job.vehicle?.placa || 'Sin placa'}
                      </span>
                      {job.priority_id !== JobPriority.NORMAL && (
                        <span className="flex items-center gap-1 px-2 py-0.5 text-xs bg-red-100 text-red-700 rounded">
                          <AlertTriangle className="h-3 w-3" />
                          {getPriorityName(job.priority_id as JobPriority)}
                        </span>
                      )}
                    </div>
                    <span className={`px-2 py-0.5 text-xs text-white rounded ${getJobEstadoColor(job.estado_id as JobEstado)}`}>
                      {getJobEstadoName(job.estado_id as JobEstado)}
                    </span>
                  </div>

                  <div className="grid grid-cols-3 gap-4 text-sm text-gray-500">
                    <div>
                      <span className="font-medium">Vehículo:</span>{' '}
                      {job.vehicle?.marca} {job.vehicle?.modelo}
                    </div>
                    <div>
                      <span className="font-medium">Cliente:</span> {job.cliente_nombre}
                    </div>
                    <div className="flex items-center gap-1">
                      <Clock className="h-4 w-4" />
                      {format(new Date(job.fecha_ingreso), 'dd MMM HH:mm', { locale: es })}
                    </div>
                  </div>

                  {/* Progress */}
                  {job.steps && job.steps.length > 0 && (
                    <div className="mt-3">
                      <div className="flex items-center gap-2 mb-1">
                        <div className="flex-1 bg-gray-200 rounded-full h-2">
                          <div
                            className="bg-blue-600 h-2 rounded-full transition-all"
                            style={{
                              width: `${(job.steps.filter(s => s.status_id === 40).length / job.steps.length) * 100}%`,
                            }}
                          />
                        </div>
                        <span className="text-xs text-gray-500">
                          {job.steps.filter(s => s.status_id === 40).length}/{job.steps.length} etapas
                        </span>
                      </div>
                    </div>
                  )}
                </div>
              ))
            )}
          </div>
        </div>

        {/* Detalle del Job */}
        <div className="bg-white rounded-xl shadow-sm">
          {selectedJob ? (
            <div className="p-4">
              <div className="flex items-center justify-between mb-4">
                <h2 className="text-lg font-semibold text-gray-800">
                  {selectedJob.vehicle?.placa || 'Job'}
                </h2>
                <span className={`px-2 py-0.5 text-xs text-white rounded ${getJobEstadoColor(selectedJob.estado_id as JobEstado)}`}>
                  {getJobEstadoName(selectedJob.estado_id as JobEstado)}
                </span>
              </div>

              {/* Vehicle Info */}
              <div className="bg-gray-50 rounded-lg p-3 mb-4">
                <p className="font-medium text-gray-800">
                  {selectedJob.vehicle?.marca} {selectedJob.vehicle?.modelo} {selectedJob.vehicle?.anio}
                </p>
                <p className="text-sm text-gray-500">{selectedJob.vehicle?.color}</p>
                <p className="text-sm text-gray-500 mt-2">
                  <span className="font-medium">Cliente:</span> {selectedJob.cliente_nombre}
                </p>
                <p className="text-sm text-gray-500">
                  <span className="font-medium">Tel:</span> {selectedJob.cliente_telefono}
                </p>
              </div>

              {/* Etapas */}
              <div className="mb-4">
                <h3 className="text-sm font-semibold text-gray-700 mb-2">Proceso</h3>
                <div className="space-y-2">
                  {selectedJob.steps?.map((step) => (
                    <div
                      key={step.id}
                      className="flex items-center justify-between p-2 bg-gray-50 rounded-lg"
                    >
                      <div className="flex items-center gap-2">
                        {step.status_id === 40 ? (
                          <CheckCircle className="h-5 w-5 text-green-500" />
                        ) : step.status_id === 20 ? (
                          <div className="h-5 w-5 rounded-full border-2 border-blue-500 animate-pulse" />
                        ) : (
                          <div className="h-5 w-5 rounded-full border-2 border-gray-300" />
                        )}
                        <span className={`text-sm ${
                          step.status_id === 40 ? 'text-green-600' :
                          step.status_id === 20 ? 'text-blue-600' : 'text-gray-500'
                        }`}>
                          {step.etapa?.nombre || 'Etapa'}
                        </span>
                      </div>
                      {step.status_id === 20 && (
                        <button
                          onClick={() => handleCompleteStep(selectedJob.id, step.id)}
                          className="px-3 py-1 bg-green-600 text-white text-xs rounded hover:bg-green-700"
                        >
                          Completar
                        </button>
                      )}
                      {step.status_id === 10 && (
                        <button
                          onClick={() => handleStartStep(selectedJob.id, step.id)}
                          className="px-3 py-1 bg-blue-600 text-white text-xs rounded hover:bg-blue-700"
                        >
                          Iniciar
                        </button>
                      )}
                    </div>
                  ))}
                </div>
              </div>

              {/* Asignar Operario */}
              {selectedJob.estado_id === JobEstado.RECIBIDO ||
               selectedJob.estado_id === JobEstado.DIAGNOSTICO ? (
                <div className="mb-4">
                  <h3 className="text-sm font-semibold text-gray-700 mb-2">Asignar Operario</h3>
                  <select
                    onChange={(e) => {
                      if (e.target.value) {
                        handleAssignOperario(selectedJob.id, e.target.value);
                      }
                    }}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm"
                    defaultValue=""
                  >
                    <option value="">Seleccionar operario...</option>
                    {operariosLibres.map((status) => (
                      <option key={status.id} value={status.user_id}>
                        {status.user?.first_name} {status.user?.last_name}
                      </option>
                    ))}
                  </select>
                </div>
              ) : null}

              {/* Asignados */}
              {selectedJob.assignedUsers && selectedJob.assignedUsers.length > 0 && (
                <div>
                  <h3 className="text-sm font-semibold text-gray-700 mb-2">Equipo Asignado</h3>
                  <div className="space-y-2">
                    {selectedJob.assignedUsers.map((user) => (
                      <div key={user.id} className="flex items-center gap-2 p-2 bg-gray-50 rounded">
                        <User className="h-4 w-4 text-gray-400" />
                        <span className="text-sm">{user.first_name} {user.last_name}</span>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          ) : (
            <div className="p-8 text-center text-gray-500">
              <Car className="h-12 w-12 mx-auto mb-3 text-gray-300" />
              <p>Selecciona un job para ver detalles</p>
            </div>
          )}
        </div>
      </div>

      {/* Estado de Operarios */}
      <div className="mt-6 bg-white rounded-xl shadow-sm p-4">
        <h2 className="text-lg font-semibold text-gray-800 mb-4 flex items-center gap-2">
          <Users className="h-5 w-5" />
          Estado de Operarios
        </h2>
        <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-3">
          {operarioStatuses.map((status) => (
            <div key={status.id} className="p-3 bg-gray-50 rounded-lg">
              <div className="flex items-center gap-2 mb-1">
                <span className={`w-2 h-2 rounded-full ${getOperarioEstadoColor(status.status_id as OperarioEstado)}`}></span>
                <span className="font-medium text-sm truncate">
                  {status.user?.first_name} {status.user?.last_name?.charAt(0)}.
                </span>
              </div>
              <p className="text-xs text-gray-500">
                {getOperarioEstadoName(status.status_id as OperarioEstado)}
              </p>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
